import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { db, storage } from "../firebase";
import { collection, addDoc, serverTimestamp, getDocs, query, where } from "firebase/firestore";
import { ImagePlus, Camera, X, CheckCircle } from "lucide-react";
import { containsBadWords } from "../utils/contentFilter";
import * as nsfwjs from "nsfwjs";
import Swal from "sweetalert2";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";


export default function MarketForm({ onClose, onSuccess }) {
  const { currentUser } = useAuth();

  const [localPreviews, setLocalPreviews] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const modelRef = useRef(null);
  const [loadingNSFW, setLoadingNSFW] = useState(false);


  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Tecnología",
    condition: "Nuevo",
    quantity: 1,
    delivery: "Envío",
    warranty: "No",
    price: "",
    paymentMethods: [],
  });

  const categories = ["Tecnología", "Hogar", "Libros", "Ropa", "Accesorios", "Materiales", "Instrumentos"];
  const conditions = ["Nuevo", "Usado"];
  const deliveries = ["Envío", "Recoger en el campus"];
  const paymentOptions = ["Transferencia", "Efectivo", "Cheque", "Paypal"];

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const loadModel = async () => {
      setLoadingNSFW(true);
      modelRef.current = await nsfwjs.load();
      setLoadingNSFW(false);
    };
    loadModel();
  }, []);


const isFileNSFW = async (file) => {
  if (!modelRef.current) return false; // Modelo no cargado todavía

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const url = reader.result;
      const isVideo = file.type.startsWith("video");
      const mediaElement = document.createElement(isVideo ? "video" : "img");
      mediaElement.src = url;

      // Necesario para imágenes
      if (!isVideo) {
        mediaElement.onload = async () => {
          const predictions = await modelRef.current.classify(mediaElement);
          const nsfw = predictions.find(
            (p) => ["Porn", "Hentai", "Sexy"].includes(p.className) && p.probability > 0.7
          );
          resolve(!!nsfw);
        };
      } else {
        // Para videos
        mediaElement.onloadeddata = async () => {
          const predictions = await modelRef.current.classify(mediaElement);
          const nsfw = predictions.find(
            (p) => ["Porn", "Hentai", "Sexy"].includes(p.className) && p.probability > 0.7
          );
          resolve(!!nsfw);
        };
      }
    };
    reader.readAsDataURL(file);
  });
};


  const handlePaymentChange = (option) => {
    setFormData((prev) => {
      const exists = prev.paymentMethods.includes(option);
      const updated = exists
        ? prev.paymentMethods.filter((p) => p !== option)
        : [...prev.paymentMethods, option];
      return { ...prev, paymentMethods: updated };
    });
  };

  const handleLocalPreviews = async (files) => {
  if (!files || files.length === 0) return;

  const previews = [];
  const maxFiles = 6;

  for (let i = 0; i < files.length && previews.length < maxFiles; i++) {
    const file = files[i];

    // NSFW check
    setLoadingNSFW(true);
    const nsfw = await isFileNSFW(file);
    setLoadingNSFW(false);

    if (nsfw) {
      Swal.fire("Contenido bloqueado", "Este archivo contiene contenido no permitido (NSFW).", "error");
      continue;
    }

    const url = URL.createObjectURL(file);
    previews.push({ file, url });
  }

  setLocalPreviews(previews);
};


  const removeLocalImage = (index) => {
    setLocalPreviews((prev) => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadToFirebase = async () => {
  if (localPreviews.length === 0) return [];
  setLoadingMedia(true);
  const uploadedUrls = [];

  try {
    for (const item of localPreviews) {
      const file = item.file;
      if (!file) continue;

      // Crea un nombre único en la carpeta "market"
      const filePath = `market/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);

      // Sube el archivo a Firebase
      const snapshot = await uploadBytes(storageRef, file);

      // Obtiene la URL pública
      const downloadURL = await getDownloadURL(snapshot.ref);

      uploadedUrls.push(downloadURL);
    }
  } catch (err) {
    console.error("Error al subir imagen a Firebase:", err);
    throw new Error("Error al subir imágenes.");
  } finally {
    setLoadingMedia(false);
  }

  return uploadedUrls;
};

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError("");
  setSuccess(false);

  const trimmedTitle = formData.title?.trim() || "";
  const trimmedDescription = formData.description?.trim() || "";

  if (!trimmedTitle || !trimmedDescription || !formData.price) {
    return setError("Todos los campos son obligatorios.");
  }

  if (containsBadWords(trimmedTitle)) {
    return setError("El título contiene palabras ofensivas o inapropiadas.");
  }

  if (containsBadWords(trimmedDescription)) {
    return setError("La descripción contiene palabras ofensivas o inapropiadas.");
  }

  if (isNaN(formData.price) || Number(formData.price) <= 0) {
    return setError("El precio debe ser un número válido.");
  }

  if (Number(formData.quantity) <= 0) {
    return setError("La cantidad debe ser mayor a 0.");
  }

  if (!currentUser) {
    return setError("Debes estar autenticado para publicar.");
  }

  if (localPreviews.length === 0) {
    return setError("Debes seleccionar al menos una imagen del producto.");
  }

  // ── Límite de 5 publicaciones activas por usuario ──────────────────────────
  try {
    const userProductsSnap = await getDocs(
      query(collection(db, "market"), where("userId", "==", currentUser.uid))
    );
    const activeCount = userProductsSnap.docs.filter((d) => {
      const s = d.data().status;
      return s !== "Vendido" && s !== "Eliminado" && s !== "eliminado";
    }).length;

    if (activeCount >= 5) {
      return Swal.fire({
        icon: "info",
        title: "🏷️ Límite de publicaciones alcanzado",
        html: `
          <p style="font-size:14px;color:#4b5563;line-height:1.6;margin-bottom:12px;">
            Tu plan actual permite hasta <strong>5 publicaciones activas</strong> en el marketplace.
            Actualmente tienes <strong>${activeCount} publicaciones activas</strong>.
          </p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;">
            ¿Necesitas publicar más productos? Puedes <strong>marcar una publicación como vendida o eliminarla</strong>
            para liberar espacio, o bien contactar a soporte para ampliar tu plan a un límite mayor.
          </p>
          <p style="font-size:12px;color:#9ca3af;margin-top:10px;">
            📧 soporte@sit-app.com
          </p>`,
        confirmButtonText: "Entendido",
        confirmButtonColor: "#6366f1",
      });
    }
  } catch (limitErr) {
    console.warn("[MarketForm] No se pudo verificar el límite de publicaciones:", limitErr.message);
    // No bloquear al usuario si la verificación falla
  }
  // ────────────────────────────────────────────────────────────────────────────

  setSubmitting(true);

  try {
    const mediaUrls = await uploadToFirebase();

    const newDoc = {
      ...formData,
      title: trimmedTitle,
      description: trimmedDescription,
      quantity: Number(formData.quantity),
      price: Number(formData.price),
      media: mediaUrls,
      userId: currentUser.uid,
      userEmail: currentUser.email,
      userName: currentUser.displayName || "",
      createdAt: serverTimestamp(),
      type: "venta",
      status: "Disponible",
    };

    await addDoc(collection(db, "market"), newDoc);
    setSuccess(true);
    if (onSuccess) onSuccess();
  } catch (err) {
    console.error("Error al publicar:", err);
    setError("Error al guardar la publicación.");
  } finally {
    setSubmitting(false);
  }
};


  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-4 sm:p-6 rounded-xl shadow-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 max-w-3xl mx-auto transition-colors">
      <h2 className="text-2xl font-bold text-center">Publicar artículo</h2>

      {error && <p className="text-red-500 text-sm text-center">{error}</p>}
      {success && (
        <div className="flex items-center justify-center bg-green-100 border border-green-400 text-green-700 rounded p-3 gap-2">
          <CheckCircle className="w-5 h-5" />
          <p>¡Tu producto ha sido publicado exitosamente!</p>
        </div>
      )}

      {/* Imagenes */}
      <div>
        <label className="font-semibold block mb-2">Imágenes del producto (máx 6)</label>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={() => fileInputRef.current.click()}
            className="flex items-center justify-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition w-full sm:w-auto"
          >
            <ImagePlus className="w-5 h-5" />
            Subir imágenes
          </button>

          <button
            type="button"
            onClick={() => fileInputRef.current.setAttribute("capture", "environment")}
            className="flex items-center justify-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition w-full sm:w-auto"
          >
            <Camera className="w-5 h-5" />
            Tomar foto
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleLocalPreviews(e.target.files)}
          className="hidden"
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
          {localPreviews.map((img, i) => (
            <div key={i} className="relative group border rounded overflow-hidden">
              <img src={img.url} alt={`Vista previa ${i + 1}`} className="w-full h-28 object-contain" />
              <button
                type="button"
                onClick={() => removeLocalImage(i)}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Campos de texto */}
      <div>
        <label className="font-semibold">Título del producto</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="w-full p-3 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
          required
        />
      </div>

      <div>
        <label className="font-semibold">Descripción</label>
        <textarea
          name="description"
          rows="4"
          value={formData.description}
          onChange={handleChange}
          className="w-full p-3 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
          required
        />
      </div>

      {/* Selects */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="font-semibold">Categoría</label>
          <select name="category" value={formData.category} onChange={handleChange} className="w-full p-3 border rounded dark:bg-gray-700 dark:border-gray-600">
            {categories.map((cat) => <option key={cat}>{cat}</option>)}
          </select>
        </div>

        <div>
          <label className="font-semibold">Condición</label>
          <select name="condition" value={formData.condition} onChange={handleChange} className="w-full p-3 border rounded dark:bg-gray-700 dark:border-gray-600">
            {conditions.map((cond) => <option key={cond}>{cond}</option>)}
          </select>
        </div>
      </div>

      {/* Cantidad y Precio */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="font-semibold">Cantidad</label>
          <input
            type="number"
            name="quantity"
            min={1}
            value={formData.quantity}
            onChange={handleChange}
            className="w-full p-3 border rounded dark:bg-gray-700 dark:border-gray-600"
          />
        </div>

        <div>
          <label className="font-semibold">Precio (RD$)</label>
          <input
            type="number"
            name="price"
            min={1}
            value={formData.price}
            onChange={handleChange}
            className="w-full p-3 border rounded dark:bg-gray-700 dark:border-gray-600"
          />
        </div>
      </div>

      {/* Entrega y Garantía */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="font-semibold">Entrega</label>
          <select name="delivery" value={formData.delivery} onChange={handleChange} className="w-full p-3 border rounded dark:bg-gray-700 dark:border-gray-600">
            {deliveries.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>

        <div>
          <label className="font-semibold">Garantía</label>
          <select name="warranty" value={formData.warranty} onChange={handleChange} className="w-full p-3 border rounded dark:bg-gray-700 dark:border-gray-600">
            <option>Sí</option>
            <option>No</option>
          </select>
        </div>
      </div>

      {/* Métodos de Pago */}
      <div>
        <label className="font-semibold block mb-2">Métodos de pago</label>
        <div className="flex flex-wrap gap-3">
          {paymentOptions.map((option) => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.paymentMethods.includes(option)}
                onChange={() => handlePaymentChange(option)}
              />
              {option}
            </label>
          ))}
        </div>
      </div>

      {/* Botones */}
      <div className="flex flex-col sm:flex-row justify-between pt-4 gap-4">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 w-full sm:w-auto"
        >
          Cancelar
        </button>

        <button
          type="submit"
          disabled={submitting || loadingMedia}
          className={`px-6 py-2 rounded text-white w-full sm:w-auto ${
            submitting || loadingMedia ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {submitting ? "Publicando..." : "Publicar"}
        </button>
      </div>
    </form>
  );
}
